import { useEffect, useState } from "react";
import { Sparkles, Trash2, Plus } from "lucide-react";
import { PageHeader } from "@/shared/components/PageHeader";
import { Card } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { InfoHint } from "@/shared/components/ui/info-hint";
import { cn } from "@/shared/lib/utils";
import { useAuth } from "@/shared/auth/useAuth";
import { getActiveTenantId } from "@/shared/tenant/active-tenant";
import {
  useAiProfile,
  useBrandKit,
  useCreateSkill,
  useDeleteSkill,
  usePreview,
  useSkills,
  useUpdateAiProfile,
  useUpdateBrandKit,
  useUpdateSkill,
} from "./use-ai-profile";
import type { AiProfile, AiSkill, BrandKit, SkillScope, Surface } from "./types";

type Tab = "rules" | "brand" | "skills";
const TABS: { id: Tab; label: string }[] = [
  { id: "rules", label: "Rules" },
  { id: "brand", label: "Brand kit" },
  { id: "skills", label: "Skills" },
];

/**
 * AI & Brand (ADR-0018): a tenant admin shapes how the AI represents their
 * business — voice/rules, brand kit, and reusable skills — feeding both the
 * assistant and the outbound copy. Editable by TENANT_ADMIN/SUPERADMIN; the API
 * re-enforces. A live preview shows the composed block.
 */
export function AiProfilePage() {
  const { user } = useAuth();
  const canEdit = !!user && (user.superadmin || user.role === "TENANT_ADMIN");
  const [tab, setTab] = useState<Tab>("rules");
  // A profile belongs to one tenant. Remount the tabs + preview when the active
  // tenant changes so their once-initialized form state can't leak across tenants.
  const tenantId = getActiveTenantId() ?? "none";

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="AI & Brand"
        footnote="Teach the AI your business, voice and brand. It shapes the assistant and the cold-outreach copy your workspace generates."
      />

      {!canEdit && (
        <p className="mt-4 text-[13px] text-ge-muted" data-testid="ai-profile-readonly">
          Only a workspace admin can change these. You&apos;re viewing them read-only.
        </p>
      )}

      <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        <div>
          <div className="mb-4 flex gap-1 border-b border-ge-border-subtle" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                data-testid={`ai-tab-${t.id}`}
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                className={cn(
                  "border-b-2 px-3 py-2 text-[13px] transition-colors",
                  tab === t.id
                    ? "border-ge-accent font-semibold text-ge-primary"
                    : "border-transparent font-medium text-ge-secondary hover:text-ge-primary",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "rules" && <RulesTab key={`rules-${tenantId}`} canEdit={canEdit} />}
          {tab === "brand" && <BrandTab key={`brand-${tenantId}`} canEdit={canEdit} />}
          {tab === "skills" && <SkillsTab key={`skills-${tenantId}`} canEdit={canEdit} />}
        </div>

        <PreviewPanel key={`preview-${tenantId}`} />
      </div>
    </div>
  );
}

// ── Rules ──────────────────────────────────────────────────────────────────────
function RulesTab({ canEdit }: { canEdit: boolean }) {
  const q = useAiProfile();
  const update = useUpdateAiProfile();
  const [form, setForm] = useState<AiProfile | null>(null);
  useEffect(() => {
    if (q.data && !form) setForm(q.data);
  }, [q.data, form]);

  function set<K extends keyof AiProfile>(k: K, v: AiProfile[K]) {
    setForm((f) => (f ? { ...f, [k]: v } : f));
  }
  if (!form) return <Loading />;

  return (
    <Card className="space-y-5 p-5">
      <Row label="Enabled" hint="Master switch — apply this profile to the AI. Off = generic behavior.">
        <Toggle checked={!!form.enabled} disabled={!canEdit} onChange={(v) => set("enabled", v)} testId="rules-enabled" />
      </Row>
      <TextArea label="What your business does" hint="Positioning, what you sell, why it matters." value={form.businessProfile ?? ""} disabled={!canEdit} onChange={(v) => set("businessProfile", v)} testId="rules-business" />
      <TextArea label="Target audience (ICP)" hint="Who you're reaching out to." value={form.audience ?? ""} disabled={!canEdit} onChange={(v) => set("audience", v)} testId="rules-audience" />
      <TextArea label="Voice & tone" hint="How you sound — e.g. warm, concise, never pushy." value={form.voice ?? ""} disabled={!canEdit} onChange={(v) => set("voice", v)} testId="rules-voice" />
      <TagRow label="Value propositions" hint="Your selling points — what makes you worth a reply. Press Enter to add each." value={form.valueProps ?? []} disabled={!canEdit} onChange={(v) => set("valueProps", v)} testId="rules-valueprops" />
      <TagRow label="Do" hint="Things the AI should always do in copy (e.g. one clear CTA)." value={form.dos ?? []} disabled={!canEdit} onChange={(v) => set("dos", v)} testId="rules-dos" />
      <TagRow label="Don't" hint="Things the AI should avoid (e.g. no hype, no false urgency)." value={form.donts ?? []} disabled={!canEdit} onChange={(v) => set("donts", v)} testId="rules-donts" />
      <TagRow label="Banned words" hint="Never used in generated copy (enforced on the output too)." value={form.bannedWords ?? []} disabled={!canEdit} onChange={(v) => set("bannedWords", v)} testId="rules-banned" />
      <TextArea label="Additional instructions" value={form.customInstructions ?? ""} disabled={!canEdit} onChange={(v) => set("customInstructions", v)} testId="rules-custom" />
      <TextArea
        label="Subject line structure"
        hint="A formula the AI follows for every email subject. Use merge fields in double braces, e.g. {{name}} and WanderFitness. Known fields: {{name}}, {{city}}, {{rating}}, {{website}}, {{address}}. Leave blank to let the AI choose."
        value={form.subjectFormat ?? ""}
        disabled={!canEdit}
        onChange={(v) => set("subjectFormat", v)}
        testId="rules-subjectformat"
      />
      <Field
        label="Follow-up strategy"
        hint="One line per email in a sequence, in order (line 1 = first email, line 2 = follow-up, …). Each line guides that step's copy — e.g. 'Intro: lead with a benefit', 'Follow-up: shorter, reference the first email', 'Breakup: last touch, low pressure'."
      >
        <textarea
          data-testid="rules-followup"
          rows={4}
          disabled={!canEdit}
          value={(form.followupStrategy ?? []).join("\n")}
          onChange={(e) => set("followupStrategy", e.target.value.split("\n"))}
          className="w-full resize-none rounded-ge-control border border-ge-border-card bg-transparent px-3 py-2 text-[13px] text-ge-primary placeholder:text-ge-muted focus-visible:outline-none disabled:opacity-60"
        />
      </Field>
      <SaveBar canEdit={canEdit} pending={update.isPending} onSave={() => update.mutate(form)} testId="rules-save" />
    </Card>
  );
}

// ── Brand kit ──────────────────────────────────────────────────────────────────
function BrandTab({ canEdit }: { canEdit: boolean }) {
  const q = useBrandKit();
  const update = useUpdateBrandKit();
  const [form, setForm] = useState<BrandKit | null>(null);
  useEffect(() => {
    if (q.data && !form) setForm(q.data);
  }, [q.data, form]);

  function set<K extends keyof BrandKit>(k: K, v: BrandKit[K]) {
    setForm((f) => (f ? { ...f, [k]: v } : f));
  }
  if (!form) return <Loading />;

  return (
    <Card className="space-y-5 p-5">
      <Field label="From name" hint="The sender name recipients see (e.g. your name or the company)."><Input data-testid="brand-fromname" disabled={!canEdit} value={form.fromName ?? ""} onChange={(e) => set("fromName", e.target.value)} /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Primary color" hint="Brand accent used in generated HTML. Hex, e.g. #0af."><Input data-testid="brand-primary" disabled={!canEdit} placeholder="#0af" value={form.primaryColor ?? ""} onChange={(e) => set("primaryColor", e.target.value)} /></Field>
        <Field label="Secondary color" hint="Supporting brand color. Hex, e.g. #123."><Input disabled={!canEdit} placeholder="#123" value={form.secondaryColor ?? ""} onChange={(e) => set("secondaryColor", e.target.value)} /></Field>
      </div>
      <Field label="Logo URL" hint="A hosted image URL (https)."><Input data-testid="brand-logo" disabled={!canEdit} value={form.logoUrl ?? ""} onChange={(e) => set("logoUrl", e.target.value)} /></Field>
      <Field label="Website" hint="Your public site (https). May appear in copy and signatures."><Input disabled={!canEdit} value={form.website ?? ""} onChange={(e) => set("website", e.target.value)} /></Field>
      <TextArea label="Email signature (text)" hint="Plain-text sign-off appended to generated emails." value={form.signatureText ?? ""} disabled={!canEdit} onChange={(v) => set("signatureText", v)} testId="brand-signature" />
      <TextArea label="Email signature (HTML)" hint="Sanitized on save — scripts and unsafe URLs are stripped." value={form.signatureHtml ?? ""} disabled={!canEdit} onChange={(v) => set("signatureHtml", v)} testId="brand-signature-html" />
      <TextArea label="Legal footer" hint="Postal address / unsubscribe copy (CAN-SPAM)." value={form.legalFooter ?? ""} disabled={!canEdit} onChange={(v) => set("legalFooter", v)} testId="brand-footer" />
      <SaveBar canEdit={canEdit} pending={update.isPending} onSave={() => update.mutate(form)} testId="brand-save" />
    </Card>
  );
}

// ── Skills ─────────────────────────────────────────────────────────────────────
const SCOPES: SkillScope[] = ["ALL", "COPY", "ASSISTANT", "ADS"];

function SkillsTab({ canEdit }: { canEdit: boolean }) {
  const q = useSkills();
  const create = useCreateSkill();
  const update = useUpdateSkill();
  const del = useDeleteSkill();
  const [draft, setDraft] = useState({ name: "", instructions: "", scope: "ALL" as SkillScope });

  if (!q.data) return <Loading />;

  return (
    <div className="space-y-4">
      {q.data.length === 0 && (
        <p className="text-[13px] text-ge-muted" data-testid="skills-empty">
          No skills yet. Add reusable instruction bundles the AI can apply.
        </p>
      )}

      {q.data.map((s: AiSkill) => (
        <Card key={s.id} className="flex items-start justify-between gap-3 p-4" >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-[13.5px] font-semibold text-ge-primary">{s.name}</p>
              <span className="rounded-ge-pill border border-ge-border-card px-2 py-[1px] font-mono text-[10px] text-ge-secondary">{s.scope}</span>
            </div>
            {s.description && <p className="mt-1 text-[12px] text-ge-secondary">{s.description}</p>}
            <p className="mt-1 line-clamp-2 text-[12px] text-ge-muted">{s.instructions}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Toggle checked={s.enabled} disabled={!canEdit} onChange={(v) => update.mutate({ id: s.id, patch: { enabled: v } })} testId={`skill-toggle-${s.id}`} />
            {canEdit && (
              <button data-testid={`skill-delete-${s.id}`} onClick={() => del.mutate(s.id)} aria-label="Delete skill" className="text-ge-secondary hover:text-ge-danger">
                <Trash2 className="h-[15px] w-[15px]" strokeWidth={1.8} />
              </button>
            )}
          </div>
        </Card>
      ))}

      {canEdit && (
        <Card className="space-y-3 p-4">
          <p className="text-[12.5px] font-semibold text-ge-primary">Add a skill</p>
          <Input data-testid="skill-new-name" placeholder="Name — e.g. Cold email for boutique gyms" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
          <textarea data-testid="skill-new-instructions" rows={3} placeholder="Instructions the AI applies when this skill is relevant…" value={draft.instructions} onChange={(e) => setDraft((d) => ({ ...d, instructions: e.target.value }))} className="w-full resize-none rounded-ge-control border border-ge-border-card bg-transparent px-3 py-2 text-[13px] text-ge-primary placeholder:text-ge-muted focus-visible:outline-none" />
          <div className="flex items-center justify-between">
            <Select value={draft.scope} options={SCOPES} onChange={(v) => setDraft((d) => ({ ...d, scope: v as SkillScope }))} testId="skill-new-scope" />
            <Button
              data-testid="skill-new-save"
              disabled={create.isPending || !draft.name.trim() || !draft.instructions.trim()}
              onClick={() =>
                create.mutate(
                  { name: draft.name.trim(), instructions: draft.instructions.trim(), scope: draft.scope },
                  { onSuccess: () => setDraft({ name: "", instructions: "", scope: "ALL" }) },
                )
              }
            >
              <Plus className="h-[14px] w-[14px]" strokeWidth={2.2} /> Add
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Preview ────────────────────────────────────────────────────────────────────
function PreviewPanel() {
  const [surface, setSurface] = useState<Surface>("COPY");
  const preview = usePreview(surface);
  return (
    <Card className="h-fit space-y-3 p-4 lg:sticky lg:top-4">
      <div className="flex items-center gap-2">
        <Sparkles className="h-[15px] w-[15px] text-ge-accent" strokeWidth={2} />
        <p className="text-[13px] font-semibold text-ge-primary">Live preview</p>
      </div>
      <p className="text-[12px] text-ge-muted">The exact tenant block prepended to the AI on each surface.</p>
      <Select value={surface} options={["COPY", "ASSISTANT", "ADS"]} onChange={(v) => setSurface(v as Surface)} testId="preview-surface" />
      <pre data-testid="preview-block" className="max-h-[420px] overflow-auto whitespace-pre-wrap rounded-ge-control border border-ge-border-card bg-ge-card px-3 py-2 font-mono text-[11px] text-ge-value">
        {preview.data?.block?.trim() || "— nothing configured for this surface yet —"}
      </pre>
    </Card>
  );
}

// ── Small local controls (design-system tokens; no shared Toggle/Select yet) ────
function Loading() {
  return <p className="text-[13px] text-ge-muted">Loading…</p>;
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 flex items-center gap-1">
        <label className="block text-[12.5px] font-medium text-ge-primary">{label}</label>
        {hint && <InfoHint text={hint} label={`About ${label}`} />}
      </div>
      {children}
    </div>
  );
}

function TextArea({ label, hint, value, disabled, onChange, testId }: { label: string; hint?: string; value: string; disabled?: boolean; onChange: (v: string) => void; testId?: string }) {
  return (
    <Field label={label} hint={hint}>
      <textarea
        data-testid={testId}
        rows={3}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full resize-none rounded-ge-control border border-ge-border-card bg-transparent px-3 py-2 text-[13px] text-ge-primary placeholder:text-ge-muted focus-visible:outline-none disabled:opacity-60"
      />
    </Field>
  );
}

function Row({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <p className="flex items-center gap-1 text-[13px] font-medium text-ge-primary">
        {label}
        {hint && <InfoHint text={hint} label={`About ${label}`} />}
      </p>
      {children}
    </div>
  );
}

function Toggle({ checked, disabled, onChange, testId }: { checked: boolean; disabled?: boolean; onChange: (v: boolean) => void; testId?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative h-[22px] w-[38px] rounded-full transition-colors disabled:opacity-50",
        checked ? "bg-ge-accent" : "bg-ge-border-card",
      )}
    >
      <span className={cn("absolute top-[2px] h-[18px] w-[18px] rounded-full bg-white transition-all", checked ? "left-[18px]" : "left-[2px]")} />
    </button>
  );
}

function Select({ value, options, disabled, onChange, testId }: { value: string; options: readonly string[]; disabled?: boolean; onChange: (v: string) => void; testId?: string }) {
  return (
    <select
      data-testid={testId}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-ge-control border border-ge-border-card bg-transparent px-2 py-[6px] text-[13px] text-ge-primary focus-visible:outline-none disabled:opacity-60"
    >
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

/** Chip-list editor: type + Enter to add, click × to remove. */
function TagRow({ label, hint, value, disabled, onChange, testId }: { label: string; hint?: string; value: string[]; disabled?: boolean; onChange: (v: string[]) => void; testId?: string }) {
  const [text, setText] = useState("");
  function add() {
    const t = text.trim();
    if (!t) return;
    if (!value.includes(t)) onChange([...value, t]);
    setText("");
  }
  return (
    <Field label={label} hint={hint}>
      <div className="flex flex-wrap gap-1.5">
        {value.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 rounded-ge-pill border border-ge-border-card px-2 py-[2px] text-[12px] text-ge-value">
            {v}
            {!disabled && (
              <button type="button" aria-label={`Remove ${v}`} onClick={() => onChange(value.filter((x) => x !== v))} className="text-ge-muted hover:text-ge-danger">×</button>
            )}
          </span>
        ))}
      </div>
      {!disabled && (
        <input
          data-testid={testId}
          value={text}
          placeholder="Type and press Enter…"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          className="mt-1.5 w-full rounded-ge-control border border-ge-border-card bg-transparent px-3 py-2 text-[13px] text-ge-primary placeholder:text-ge-muted focus-visible:outline-none"
        />
      )}
    </Field>
  );
}

function SaveBar({ canEdit, pending, onSave, testId }: { canEdit: boolean; pending: boolean; onSave: () => void; testId?: string }) {
  if (!canEdit) return null;
  return (
    <div className="flex justify-end border-t border-ge-border-subtle pt-4">
      <Button data-testid={testId} disabled={pending} onClick={onSave}>
        {pending ? "Saving…" : "Save"}
      </Button>
    </div>
  );
}
